import { Countdown } from '../countdown';
import { FormatDate } from '../format/format-date';
import { ViewDateIcon, ViewDisputeIcon, ViewFinalIcon, ViewProposedIcon } from '@/client/assets';
import { DEADLINE_TIMEZONE, DEADLINE_TZ_LABEL } from '@/client/confs/app';
import { cn } from '@/client/utils';
import { MarketStatus } from '@/libs/sdk/api';
import type { Time } from 'lightweight-charts';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type MarketDeadlineProps = {
  className?: string;
  deadline?: number;
  status?: MarketStatus;
  formatStr?: string;
};

// Settlement deadlines render in one fixed zone, matching the timezone the market
// rules are written in (AIP: GST/UTC+4) — a deadline that shifts with the reader's
// device can't be reconciled against a rule that names one fixed zone. `HH:mm` (24h)
// because the dailies settle at midnight and locale 12h formats render that as
// "上午 12:00". Exported so every settlement-deadline surface (chip, market-info
// strip, ticker) renders the same fixed zone.
//
// The zone + label come from the per-app config (confs/app.ts), NOT hardcoded here:
// the DID build lives on its own branch and MUST keep SGT. If this component is
// cherry-picked to the DID branch, that branch's app.ts must supply
// DEADLINE_TIMEZONE='Asia/Singapore' / DEADLINE_TZ_LABEL='SGT'.
export const DEADLINE_TZ = DEADLINE_TIMEZONE;
export const DEADLINE_FMT = `PP HH:mm '${DEADLINE_TZ_LABEL}'`;

export const MarketDeadline = ({ className, deadline, status, formatStr = DEADLINE_FMT }: MarketDeadlineProps) => {
  const { t } = useTranslation();
  const [intervalTime, setIntervalTime] = useState(Number(deadline) - Math.ceil(Date.now() / 1e3));
  const isCountdown = intervalTime > 0 && intervalTime <= 24 * 3600;
  const ref = useRef<number>();
  useEffect(() => {
    if (!isCountdown) return;
    ref.current = setInterval(() => {
      setIntervalTime(Number(deadline) - Math.ceil(Date.now() / 1e3));
    }, 1000) as any;
    return () => {
      clearInterval(ref.current);
    };
  }, [deadline, intervalTime, isCountdown]);

  if (status == MarketStatus.settlement) {
    return (
      <div className="flex gap-1 items-center">
        <ViewProposedIcon className="size-4 fill-foreground/40" />
        <span className="whitespace-nowrap text-foreground">{t('outcome-proposed')}</span>
      </div>
    );
  }
  if (status == MarketStatus.disputed) {
    return (
      <div className="flex gap-1 items-center">
        <ViewDisputeIcon className="size-4 fill-foreground/40" />
        <span className="whitespace-nowrap text-foreground">{t('dispute-window')}</span>
      </div>
    );
  }
  if (status == MarketStatus.closed) {
    return (
      <div className="flex gap-1 items-center">
        <ViewFinalIcon className="size-4 fill-foreground/40" />
        <span className="whitespace-nowrap text-foreground">{t('final-outcome')}</span>
      </div>
    );
  }
  return (
    <div className={cn('flex items-center gap-1', className)}>
      <ViewDateIcon className="size-4 fill-current" />
      <span>
        {/* Under 24h: live countdown. Otherwise: the absolute date.
            The 24-72h band used to render a relative phrase ("后天" / "in 2 days") via
            FormatAfterDate — that reads as a guess, not a settlement date, and a daily
            market is defined by WHICH day it settles. Relative phrasing stays correct
            for past timestamps (trades/notifications still use FormatAfterDate). */}
        {isCountdown ? (
          <Countdown seconds={intervalTime} />
        ) : (
          <FormatDate time={Number(deadline) as Time} formatStr={formatStr} timeZone={DEADLINE_TZ} />
        )}
      </span>
    </div>
  );
};
